import { cn } from '@/lib/utils';

export interface IconProps {
  name: string;
  className?: string;
  filled?: boolean;
}

const sizeMap: Record<string, string> = {
  'h-3 w-3': '12px',
  'h-3.5 w-3.5': '14px',
  'h-4 w-4': '16px',
  'h-5 w-5': '20px',
  'h-6 w-6': '24px',
  'h-7 w-7': '28px',
  'h-8 w-8': '32px',
};

function extractSize(className?: string): string {
  if (!className) return '20px';
  for (const [sizeClass, size] of Object.entries(sizeMap)) {
    if (className.includes(sizeClass.split(' ')[0])) {
      return size;
    }
  }
  return '20px';
}

// Lucide to Material Symbols name mapping
// eslint-disable-next-line react-refresh/only-export-components
export const iconMap = {
  // Navigation & Layout
  LayoutDashboard: 'dashboard',
  FolderKanban: 'view_kanban',
  Bug: 'bug_report',
  ClipboardCheck: 'assignment_turned_in',
  FileText: 'description',
  Star: 'star',
  User: 'person',
  Users: 'group',
  PanelLeftClose: 'left_panel_close',
  PanelLeft: 'left_panel_open',
  LogOut: 'logout',
  Settings: 'settings',

  // Actions
  Search: 'search',
  Plus: 'add',
  Edit: 'edit',
  Eye: 'visibility',
  Pin: 'push_pin',
  PinOff: 'push_pin',
  Send: 'send',
  X: 'close',

  // Arrows & Navigation
  ChevronLeft: 'chevron_left',
  ChevronRight: 'chevron_right',
  ChevronDown: 'expand_more',
  ArrowLeft: 'arrow_back',
  ArrowUpRight: 'north_east',
  ExternalLink: 'open_in_new',

  // Status & Alerts
  AlertTriangle: 'warning',
  AlertCircle: 'error',
  CheckCircle2: 'check_circle',
  CircleDot: 'radio_button_checked',
  Info: 'info',
  HelpCircle: 'help',

  // Notifications
  Bell: 'notifications',
  BellOff: 'notifications_off',

  // Time & Activity
  Clock: 'schedule',
  Calendar: 'calendar_today',
  Activity: 'monitoring',
  TrendingUp: 'trending_up',

  // Media Controls
  Play: 'play_arrow',
  RotateCcw: 'refresh',

  // Communication
  Mail: 'mail',
  MessageSquare: 'chat',

  // Data & Content
  FolderOpen: 'folder_open',
  Tag: 'label',
  Building2: 'business',
  TestTube: 'science',

  // Checkboxes
  CheckSquare: 'check_box',
  Square: 'check_box_outline_blank',

  // User Management
  UserMinus: 'person_remove',
  PenLine: 'edit_note',

  // Grid & Layout
  LayoutGrid: 'grid_view',

  // Loading
  Loader2: 'progress_activity',
} as const;

export type IconName = keyof typeof iconMap;

export function Icon({ name, className, filled = false }: IconProps) {
  const size = extractSize(className);
  const isAnimated = className?.includes('animate-spin');
  // Map Lucide icon names to Material Symbols names, or use the name directly
  const iconName = iconMap[name as IconName] ?? name;

  return (
    <span
      className={cn(
        'material-symbols-outlined inline-flex items-center justify-center select-none',
        isAnimated && 'animate-spin',
        className,
      )}
      style={{
        fontSize: size,
        width: size,
        height: size,
        fontVariationSettings: filled ? "'FILL' 1" : undefined,
      }}
    >
      {iconName}
    </span>
  );
}
